import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { createPortalClient } from '@/services/portalClient';
import { PostCardAuthProvider } from '@/domains/PostCreator/contexts/PostCardAuthContext';
import { UserRole } from '@/services/urlService';
import { TopPostsSection } from '@/domains/Reporting/components/TopPostsSection';
import type { TopPost } from '@/domains/Reporting/hooks/useSocialMediaReport';
import { SpinnerIcon } from '@/components/icons';

interface ApprovalOutletContext {
  clientId: string;
  /** Portal magic-link token. Used by PostCardAuthProvider so the
   *  MediaItem pipeline can sign streaming URLs for the embedded
   *  post thumbnails. Provided by ApprovalView under the key `token`. */
  token?: string;
}

interface MetricWithChange {
  value: number;
  change: number | null;
  percentage: number | null;
}

/** The portal report endpoint now returns the same `top_posts` shape
 *  as the admin endpoint (grouped by internal post, hydrated with the
 *  full Post dict + per-platform permalinks). This lets the client
 *  portal reuse the exact same TopPostsSection component as /reporting. */
interface ClientSummary {
  period: {
    year: number;
    month: number;
    start: string;
    end: string;
  };
  metrics: {
    totalPosts: MetricWithChange;
    totalImpressions: MetricWithChange;
    totalReach: MetricWithChange;
    totalEngagement: MetricWithChange;
  };
  topPosts: TopPost[];
}

const MONTH_NAMES_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
] as const;

export const ApprovalReportRoute: React.FC = () => {
  const { clientId, token: approvalToken } = useOutletContext<ApprovalOutletContext>();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const yearParam = searchParams.get('year');
  const monthParam = searchParams.get('month');

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const portalClient = createPortalClient(clientId, 'client');
        const qs = new URLSearchParams();
        if (yearParam) qs.set('year', yearParam);
        if (monthParam) qs.set('month', monthParam);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const data = await portalClient.get(
          `/portal/client/${clientId}/report/summary${suffix}`,
        );
        setSummary(data);
      } catch (err: any) {
        setError(err.message || 'Errore nel caricamento del report');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSummary();
  }, [clientId, yearParam, monthParam]);

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4">
        <div className="flex items-center justify-center py-12">
          <SpinnerIcon className="w-8 h-8 text-indigo-600" />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 font-medium">{error || 'Nessun dato disponibile'}</p>
        </div>
      </div>
    );
  }

  const windowLabel = `${MONTH_NAMES_IT[summary.period.month - 1]} ${summary.period.year}`;

  return (
    /* Wrap everything in PostCardAuthProvider so the EditorPostCard
       inside TopPostsSection knows it's in client context: hides
       admin actions and uses the approval token to sign media URLs. */
    <PostCardAuthProvider
      userRole={UserRole.CLIENT}
      clientId={clientId}
      authToken={approvalToken}
    >
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6">
          <h1 className="text-2xl font-bold text-gray-900">{windowLabel}</h1>
          <p className="text-sm text-gray-600 mt-1">
            Performance del mese ({summary.period.start} - {summary.period.end})
          </p>
        </div>

        {/* Metrics — single-column stack per DD-025 (no multi-column card grid). */}
        <div className="grid grid-cols-1 gap-4">
          <MetricTile
            label="Impressioni"
            metric={summary.metrics.totalImpressions}
            tint="blue"
            icon={
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
          />
          <MetricTile
            label="Copertura"
            metric={summary.metrics.totalReach}
            tint="emerald"
            icon={
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />
          <MetricTile
            label="Interazioni"
            metric={summary.metrics.totalEngagement}
            tint="pink"
            icon={
              <svg className="w-5 h-5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            }
          />
        </div>

        {/* Top posts — exact same component as /reporting. The
            internal PostCardAuthProvider above routes media signing
            and admin-action gating to client mode automatically. */}
        <TopPostsSection
          topPosts={summary.topPosts}
          variant="list"
          locale="it"
        />
      </div>
    </PostCardAuthProvider>
  );
};

/* ------------------------------------------------------------------ */
/* Local helpers                                                       */
/* ------------------------------------------------------------------ */

const TINT_CLASSES = {
  blue: 'bg-blue-100',
  emerald: 'bg-emerald-100',
  pink: 'bg-pink-100',
} as const;

const MetricTile: React.FC<{
  label: string;
  metric: MetricWithChange;
  tint: keyof typeof TINT_CLASSES;
  icon: React.ReactNode;
}> = ({ label, metric, tint, icon }) => (
  <div className="bg-white rounded-2xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-gray-600 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
          {formatNumber(metric.value)}
        </p>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TINT_CLASSES[tint]}`}>
        {icon}
      </div>
    </div>
    <div className="mt-2">{renderChange(metric)}</div>
  </div>
);

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
}

function renderChange(metric: MetricWithChange): React.ReactNode {
  if (metric.change === null || metric.percentage === null) return null;
  const isPositive = metric.change > 0;
  const isNegative = metric.change < 0;
  const colorClass = isPositive
    ? 'text-emerald-600'
    : isNegative
      ? 'text-red-600'
      : 'text-gray-500';
  return (
    <div className={`flex items-center gap-1 text-xs ${colorClass}`}>
      {isPositive && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      )}
      {isNegative && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      )}
      <span className="font-medium tabular-nums">
        {Math.abs(metric.percentage)}% vs mese scorso
      </span>
    </div>
  );
}
